import { Link } from "react-router-dom";
import { Instagram, Phone, Mail } from "lucide-react";
import TikTokIcon from "@/components/icons/TikTokIcon";
import { useTr, useLocalizedPath } from "@/i18n";

const pageLinks = [
  { to: "/chi-sono", label: "Chi Sono" },
  { to: "/servizi", label: "Servizi" },
  { to: "/dj-matrimoni-lugano", label: "Matrimoni a Lugano" },
  { to: "/dj-matrimoni-ticino", label: "Matrimoni in Ticino" },
  { to: "/dj-eventi-aziendali-svizzera", label: "Eventi Aziendali" },
  { to: "/dj-compleanni-feste-private", label: "Compleanni & Feste Private" },
  { to: "/dj-festival-party-stagionali", label: "Feste di Paese & Festival" },
  { to: "/dj-zurigo", label: "Zurigo" },
  { to: "/dj-ginevra", label: "Ginevra" },
  { to: "/dj-berna", label: "Berna" },
  { to: "/dj-basilea", label: "Basilea" },
  { to: "/contatti", label: "Contatti" },
  { to: "/preventivo", label: "Preventivo Gratuito" },
  { to: "/blog", label: "Blog" },
];

export default function Footer() {
  const tr = useTr();
  const privacyPath = useLocalizedPath("/privacy");
  const terminiPath = useLocalizedPath("/termini");
  return (
    <footer className="border-t border-[rgba(226,184,87,0.2)] bg-black py-14">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div className="flex flex-col items-center md:items-start gap-4 text-center md:text-left">
          <img
            src="/assets/logo-256.png"
            alt="DJ Magix"
            width={256}
            height={256}
            className="h-16 w-16 object-contain"
            loading="lazy"
          />
          <p className="font-display tracking-[0.3em] text-[#F7DE8B] text-sm font-semibold">
            DJ MAGIX
          </p>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BFA45D]">
            {tr("hero.tagline", "Musica • Emozione • Tecnologia")}
          </p>
          <p className="text-xs text-[#9F8A52] leading-relaxed">
            {tr("footer.desc", "Set di alto profilo da Lugano-Paradiso in tutta la Svizzera. 25 anni di esperienza, oltre 1500 eventi.")}
          </p>
        </div>

        <nav aria-label="Pagine del sito" className="text-center md:text-left">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BFA45D] mb-4">
            {tr("footer.pages", "Pagine")}
          </p>
          <ul className="grid grid-cols-2 gap-2.5">
            {pageLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm text-[#E2C882]/75 hover:text-[#F7DE8B] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="text-center md:text-left">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BFA45D] mb-4">
            {tr("footer.contacts", "Contatti")}
          </p>
          <div className="flex items-center justify-center md:justify-start gap-4">
            <a
              data-testid="footer-instagram-link"
              href="https://www.instagram.com/djmagixx/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram @djmagixx"
              className="flex h-10 w-10 items-center justify-center rounded-full gold-frame text-[#E2B857] hover:bg-[#E2B857]/10 transition-colors"
            >
              <Instagram size={18} />
              <span className="sr-only">{tr("footer.follow", "Segui DJ Magix su Instagram")}</span>
            </a>
            <a
              data-testid="footer-tiktok-link"
              href="https://www.tiktok.com/@dj_magix"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok @dj_magix"
              className="flex h-10 w-10 items-center justify-center rounded-full gold-frame text-[#E2B857] hover:bg-[#E2B857]/10 transition-colors"
            >
              <TikTokIcon size={17} />
              <span className="sr-only">{tr("footer.tiktok", "Segui DJ Magix su TikTok")}</span>
            </a>
            <a
              data-testid="footer-phone-link"
              href="tel:+41764411359"
              aria-label="Telefono"
              className="flex h-10 w-10 items-center justify-center rounded-full gold-frame text-[#E2B857] hover:bg-[#E2B857]/10 transition-colors"
            >
              <Phone size={18} />
              <span className="sr-only">{tr("footer.call", "Chiama DJ Magix")}</span>
            </a>
            <a
              data-testid="footer-email-link"
              href="mailto:info@djmagix.net"
              aria-label="Email"
              className="flex h-10 w-10 items-center justify-center rounded-full gold-frame text-[#E2B857] hover:bg-[#E2B857]/10 transition-colors"
            >
              <Mail size={18} />
              <span className="sr-only">{tr("footer.mail", "Scrivi una email a DJ Magix")}</span>
            </a>
          </div>
          <p className="text-sm text-[#E2C882]/75 mt-4">076 441 13 59</p>
          <p className="text-sm text-[#E2C882]/75 mt-1">info@djmagix.net</p>
          <p className="text-xs text-[#9F8A52] mt-1">Lugano-Paradiso, Ticino — Svizzera</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-[rgba(226,184,87,0.12)] text-center" data-testid="footer-directories">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#BFA45D] mb-4">
          {tr("footer.directories", "Ci trovi anche su")}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <a
            data-testid="footer-directory-matrimonio"
            href="https://www.matrimonio.com/musica-matrimonio/dj-magix-&-maditaly--e403207"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full gold-frame px-5 py-2.5 text-xs text-[#F7DE8B] hover:bg-[#E2B857]/10 transition-colors"
          >
            Matrimonio.com <span className="text-[#E2B857]">★ 4.8/5</span>
          </a>
          <a
            data-testid="footer-directory-stagend"
            href="https://ch.stagend.com/dj-magix"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full gold-frame px-5 py-2.5 text-xs text-[#F7DE8B] hover:bg-[#E2B857]/10 transition-colors"
          >
            Stagend
          </a>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-10 pb-20 sm:pb-0 text-xs text-[#9F8A52]">
        <p>{tr("footer.rights", "© 2026 DJ Magix — Tutta Musica. Ultimo aggiornamento: ottobre 2026.")}</p>
        <Link to={privacyPath} data-testid="footer-privacy-link" className="hover:text-[#F7DE8B] transition-colors underline underline-offset-2">
          Privacy Policy
        </Link>
        <Link to={terminiPath} data-testid="footer-terms-link" className="hover:text-[#F7DE8B] transition-colors underline underline-offset-2">
          {tr("contact.terms", "Termini di Servizio")}
        </Link>
        <button
          data-testid="footer-cookie-settings"
          onClick={() => window.dispatchEvent(new Event("dj-reopen-cookies"))}
          className="hover:text-[#F7DE8B] transition-colors underline underline-offset-2"
        >
          {tr("footer.cookies", "Preferenze cookie")}
        </button>
      </div>
    </footer>
  );
}
